import { SESSION_STORAGE_KEYS } from '@/app/common/session-storage'
import { Button, buttonVariants } from '@/common/components/Button'
import { UserQueries } from '@/queries/users/queries'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import CheckmarkIcon from '@assets/check-circle.svg'
import UserCircle from '@assets/user-circle.svg'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useTaxableIncomeDocumentParams from './hooks/useTaxableIncomeDocumentParams'

export const SelectHouseholdMemberPage = () => {
  const { t: translate } = useTranslation()
  const application = useSelector(selectFamilyApplication)
  const SessionUserQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.application_id)
  )

  const { onSpouseOrUserSelect } = useTaxableIncomeDocumentParams()

  const handleVerifyClick = (member: 'user' | 'partner') => {
    onSpouseOrUserSelect(member)
  }


  const getUserFullName = () => {
    const user = SessionUserQuery.data
    return user ? `${user.first_name} ${user.last_name}` : ''
  }

  const hasUploadedApplicantDocument = sessionStorage.getItem(
    SESSION_STORAGE_KEYS.hasUploadedApplicantIncome
  )
  const hasUploadedSpouseDocument = sessionStorage.getItem(
    SESSION_STORAGE_KEYS.hasUploadedSpouseIncome
  )

  return (
    <section aria-labelledby="income-verification-title">
      <h1
        id="income-verification-title"
        className="text-xl text-center font-medium text-gray-900 mb-10 sm:mb-5"
      >
        {translate('verify_income')}
      </h1>

      <div className="flex flex-col gap-4">
        <article className="flex flex-col items-center justify-center border border-gray-200 rounded-xl align-middle pt-3 pr-5 pb-4 pl-5">
          <img
            src={hasUploadedApplicantDocument ? CheckmarkIcon : UserCircle}
            alt={translate('user_icon')}
            className="h-9 mb-1"
            aria-hidden="true"
          />
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-center text-md leading-6 font-medium">
              {getUserFullName()}
            </h2>
            <p className="text-center">{translate('connect_a_payroll')}</p>
          </div>

          <Button
            variant={hasUploadedApplicantDocument ? 'outline' : 'primary'}
            onClick={() => handleVerifyClick('user')}
            className="mt-4 text-gray-900 w-full rounded text-md leading-6 font-semibold"
            aria-label={`${translate('verify')} ${getUserFullName()}`}
          >
            {translate('verify')}
          </Button>
        </article>

        <article className="flex flex-col items-center justify-center border border-gray-200 rounded-xl align-middle pt-3 pr-5 pb-4 pl-5">
          <img
            src={hasUploadedSpouseDocument ? CheckmarkIcon : UserCircle}
            alt={translate('user_icon')}
            className="h-9 mb-1 mt-0"
            aria-hidden="true"
          />
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-center text-md leading-6 font-medium">
              {translate('your_spouse')}
            </h2>
            <p className="text-center font-inter">
              {translate('upload_income')}
            </p>
          </div>

          <Button
            variant={hasUploadedSpouseDocument ? 'outline' : 'primary'}
            onClick={() => handleVerifyClick('partner')}
            className="mt-4 text-gray-900 w-full rounded text-md leading-6 font-semibold"
            aria-label={`${translate('verify')} ${translate('your_spouse')}`}
          >
            {translate('verify')}
          </Button>
        </article>

    {
      hasUploadedApplicantDocument && hasUploadedSpouseDocument && (

        <div className="mt-10 w-full">
          <Link
            className={buttonVariants({
              variant: 'primary',
              className: 'w-full',
            })}
            to={
              AppRoutePaths.family_application.income_verification
                .success_verification
            }
          >
            {translate('continue')}
          </Link>
        </div>
      )
    }
      </div>
    </section>
  )
}
